/**
 * Offline Database Utility using IndexedDB
 * Untuk menyimpan data tagihan PDAM secara offline
 * Token offline hanya berlaku untuk tanggal hari itu
 */

const DB_NAME = 'PDAMOfflineDB';
const DB_VERSION = 1;

// Store names
const STORES = {
  BILLS: 'bills',
  OFFLINE_TOKEN: 'offline_token',
  OFFLINE_PAYMENTS: 'offline_payments',
  SETTINGS: 'settings'
};

// Open database connection
const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      // Bills store - untuk menyimpan tagihan
      if (!db.objectStoreNames.contains(STORES.BILLS)) {
        const billsStore = db.createObjectStore(STORES.BILLS, { keyPath: 'id' });
        billsStore.createIndex('no_samb', 'no_samb', { unique: false });
        billsStore.createIndex('status', 'status', { unique: false });
      }
      
      // Offline token store - untuk menyimpan token/tanggal valid
      if (!db.objectStoreNames.contains(STORES.OFFLINE_TOKEN)) {
        db.createObjectStore(STORES.OFFLINE_TOKEN, { keyPath: 'key' });
      }
      
      // Offline payments store - untuk menyimpan pembayaran offline yang belum disinkron
      if (!db.objectStoreNames.contains(STORES.OFFLINE_PAYMENTS)) {
        const paymentsStore = db.createObjectStore(STORES.OFFLINE_PAYMENTS, { keyPath: 'id' });
        paymentsStore.createIndex('synced', 'synced', { unique: false });
      }
      
      // Settings store
      if (!db.objectStoreNames.contains(STORES.SETTINGS)) {
        db.createObjectStore(STORES.SETTINGS, { keyPath: 'key' });
      }
    };
  });
};

// Get today's date string (YYYY-MM-DD) in WIB timezone
export const getTodayDateString = () => {
  const now = new Date();
  // Convert to WIB (UTC+7)
  const wibOffset = 7 * 60; // minutes
  const localOffset = now.getTimezoneOffset();
  const wibTime = new Date(now.getTime() + (wibOffset + localOffset) * 60000);
  return wibTime.toISOString().split('T')[0];
};

// Check if offline mode is valid (same day)
export const isOfflineModeValid = async () => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORES.OFFLINE_TOKEN, 'readonly');
    const store = tx.objectStore(STORES.OFFLINE_TOKEN);
    
    return new Promise((resolve, reject) => {
      const request = store.get('offline_session');
      request.onsuccess = () => {
        const data = request.result;
        if (!data) {
          resolve({ valid: false, reason: 'no_session' });
          return;
        }
        
        const today = getTodayDateString();
        if (data.date !== today) {
          resolve({ valid: false, reason: 'expired', lastDate: data.date });
          return;
        }
        
        resolve({ 
          valid: true, 
          date: data.date, 
          userId: data.userId,
          userName: data.userName,
          downloadedAt: data.downloadedAt,
          billsCount: data.billsCount
        });
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error checking offline mode:', error);
    return { valid: false, reason: 'error', error: error.message };
  }
};

// Save offline session token
export const saveOfflineSession = async (userId, userName, billsCount) => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORES.OFFLINE_TOKEN, 'readwrite');
    const store = tx.objectStore(STORES.OFFLINE_TOKEN);
    
    const sessionData = {
      key: 'offline_session',
      date: getTodayDateString(),
      userId,
      userName,
      billsCount,
      downloadedAt: new Date().toISOString()
    };
    
    await new Promise((resolve, reject) => {
      const request = store.put(sessionData);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
    
    return true;
  } catch (error) {
    console.error('Error saving offline session:', error);
    return false;
  }
};

// Clear offline session (force re-login)
export const clearOfflineSession = async () => {
  try {
    const db = await openDB();
    const tx = db.transaction([STORES.OFFLINE_TOKEN, STORES.BILLS], 'readwrite');
    
    // Clear token
    const tokenStore = tx.objectStore(STORES.OFFLINE_TOKEN);
    tokenStore.delete('offline_session');
    
    // Clear bills
    const billsStore = tx.objectStore(STORES.BILLS);
    billsStore.clear();
    
    await new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    
    return true;
  } catch (error) {
    console.error('Error clearing offline session:', error);
    return false;
  }
};

// Save bills to offline storage
export const saveBillsOffline = async (bills) => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORES.BILLS, 'readwrite');
    const store = tx.objectStore(STORES.BILLS);
    
    // Clear existing bills first
    store.clear();
    
    // Add all bills
    for (const bill of bills) {
      store.put(bill);
    }
    
    await new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    
    return true;
  } catch (error) {
    console.error('Error saving bills offline:', error);
    return false;
  }
};

// Get bills from offline storage by customer ID
export const getOfflineBills = async (noSamb) => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORES.BILLS, 'readonly');
    const store = tx.objectStore(STORES.BILLS);
    const index = store.index('no_samb');
    
    return new Promise((resolve, reject) => {
      const request = index.getAll(noSamb);
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting offline bills:', error);
    return [];
  }
};

// Get all offline bills
export const getAllOfflineBills = async () => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORES.BILLS, 'readonly');
    const store = tx.objectStore(STORES.BILLS);
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting all offline bills:', error);
    return [];
  }
};

// Update bill status locally (for offline payment)
export const updateBillStatusOffline = async (billId, newStatus) => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORES.BILLS, 'readwrite');
    const store = tx.objectStore(STORES.BILLS);
    
    return new Promise((resolve, reject) => {
      const getRequest = store.get(billId);
      getRequest.onsuccess = () => {
        const bill = getRequest.result;
        if (bill) {
          bill.status = newStatus;
          bill.updated_at = new Date().toISOString();
          const putRequest = store.put(bill);
          putRequest.onsuccess = () => resolve(true);
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          resolve(false);
        }
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  } catch (error) {
    console.error('Error updating bill status offline:', error);
    return false;
  }
};

// Save offline payment (to be synced later)
export const saveOfflinePayment = async (paymentData) => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORES.OFFLINE_PAYMENTS, 'readwrite');
    const store = tx.objectStore(STORES.OFFLINE_PAYMENTS);
    
    const payment = {
      ...paymentData,
      id: `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      synced: false,
      created_at: new Date().toISOString(),
      offline_date: getTodayDateString()
    };
    
    console.log('Saving offline payment:', payment.id, payment);
    
    await new Promise((resolve, reject) => {
      const request = store.put(payment);
      request.onsuccess = () => {
        console.log('Offline payment saved successfully:', payment.id);
        resolve();
      };
      request.onerror = () => {
        console.error('Failed to save offline payment:', request.error);
        reject(request.error);
      };
    });
    
    // Wait for transaction to complete
    await new Promise((resolve, reject) => {
      tx.oncomplete = () => {
        console.log('Transaction completed for payment:', payment.id);
        resolve();
      };
      tx.onerror = () => {
        console.error('Transaction error:', tx.error);
        reject(tx.error);
      };
    });
    
    return payment;
  } catch (error) {
    console.error('Error saving offline payment:', error);
    return null;
  }
};

// Get pending offline payments (not synced)
export const getPendingOfflinePayments = async () => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORES.OFFLINE_PAYMENTS, 'readonly');
    const store = tx.objectStore(STORES.OFFLINE_PAYMENTS);
    
    // Get all payments and filter manually (more reliable than index query for boolean)
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => {
        const allPayments = request.result || [];
        // Filter to get only unsynced payments
        const pendingPayments = allPayments.filter(p => p.synced === false || !p.synced);
        console.log(`getPendingOfflinePayments: total=${allPayments.length}, pending=${pendingPayments.length}`);
        resolve(pendingPayments);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting pending payments:', error);
    return [];
  }
};

// Mark payment as synced
export const markPaymentSynced = async (paymentId) => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORES.OFFLINE_PAYMENTS, 'readwrite');
    const store = tx.objectStore(STORES.OFFLINE_PAYMENTS);
    
    return new Promise((resolve, reject) => {
      const getRequest = store.get(paymentId);
      getRequest.onsuccess = () => {
        const payment = getRequest.result;
        if (payment) {
          payment.synced = true;
          payment.synced_at = new Date().toISOString();
          const putRequest = store.put(payment);
          putRequest.onsuccess = () => resolve(true);
          putRequest.onerror = () => reject(putRequest.error);
        } else {
          resolve(false);
        }
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  } catch (error) {
    console.error('Error marking payment synced:', error);
    return false;
  }
};

// Save admin fee setting for offline use
export const saveAdminFeeOffline = async (fee) => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORES.SETTINGS, 'readwrite');
    const store = tx.objectStore(STORES.SETTINGS);
    
    await new Promise((resolve, reject) => {
      const request = store.put({ key: 'admin_fee', value: fee });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
    
    return true;
  } catch (error) {
    console.error('Error saving admin fee:', error);
    return false;
  }
};

// Get admin fee from offline storage
export const getAdminFeeOffline = async () => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORES.SETTINGS, 'readonly');
    const store = tx.objectStore(STORES.SETTINGS);
    
    return new Promise((resolve, reject) => {
      const request = store.get('admin_fee');
      request.onsuccess = () => resolve(request.result?.value || 1500);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting admin fee:', error);
    return 1500;
  }
};

// Check if we're online
export const isOnline = () => navigator.onLine;

// Debug function to view all data in IndexedDB
export const debugIndexedDB = async () => {
  try {
    const db = await openDB();
    
    // Get all offline payments
    const paymentsPromise = new Promise((resolve) => {
      const tx = db.transaction(STORES.OFFLINE_PAYMENTS, 'readonly');
      const store = tx.objectStore(STORES.OFFLINE_PAYMENTS);
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => resolve([]);
    });
    
    // Get all bills
    const billsPromise = new Promise((resolve) => {
      const tx = db.transaction(STORES.BILLS, 'readonly');
      const store = tx.objectStore(STORES.BILLS);
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => resolve([]);
    });
    
    // Get offline session
    const sessionPromise = new Promise((resolve) => {
      const tx = db.transaction(STORES.OFFLINE_TOKEN, 'readonly');
      const store = tx.objectStore(STORES.OFFLINE_TOKEN);
      const request = store.get('offline_session');
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
    });
    
    const [allPayments, allBills, session] = await Promise.all([paymentsPromise, billsPromise, sessionPromise]);
    
    console.log('=== DEBUG IndexedDB ===');
    console.log('Session:', session);
    console.log('Total Bills:', allBills.length);
    console.log('Bills (first 3):', allBills.slice(0, 3));
    console.log('Total Payments:', allPayments.length);
    console.log('Payments:', allPayments);
    console.log('======================');
    
    return {
      session,
      billsCount: allBills.length,
      bills: allBills,
      paymentsCount: allPayments.length,
      payments: allPayments
    };
  } catch (error) {
    console.error('Debug IndexedDB error:', error);
    return null;
  }
};

// Get offline statistics
export const getOfflineStats = async () => {
  try {
    const session = await isOfflineModeValid();
    const pendingPayments = await getPendingOfflinePayments();
    const allBills = await getAllOfflineBills();
    const unpaidBills = allBills.filter(b => b.status === 'unpaid');
    
    return {
      sessionValid: session.valid,
      sessionDate: session.date,
      totalBills: allBills.length,
      unpaidBills: unpaidBills.length,
      pendingPayments: pendingPayments.length,
      downloadedAt: session.downloadedAt
    };
  } catch (error) {
    console.error('Error getting offline stats:', error);
    return null;
  }
};

// Save auth token for background sync
export const saveAuthTokenForSync = async (token) => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORES.SETTINGS, 'readwrite');
    const store = tx.objectStore(STORES.SETTINGS);
    
    await new Promise((resolve, reject) => {
      const request = store.put({ key: 'auth_token', value: token });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
    
    return true;
  } catch (error) {
    console.error('Error saving auth token:', error);
    return false;
  }
};

// Clear auth token
export const clearAuthTokenForSync = async () => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORES.SETTINGS, 'readwrite');
    const store = tx.objectStore(STORES.SETTINGS);
    
    await new Promise((resolve, reject) => {
      const request = store.delete('auth_token');
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
    
    return true;
  } catch (error) {
    console.error('Error clearing auth token:', error);
    return false;
  }
};

// Register background sync for offline payments
export const registerBackgroundSync = async () => {
  if (!('serviceWorker' in navigator) || !('SyncManager' in window)) {
    console.log('Background Sync not supported');
    return false;
  }
  
  try {
    const registration = await navigator.serviceWorker.ready;
    await registration.sync.register('sync-offline-payments');
    console.log('Background sync registered');
    return true;
  } catch (error) {
    console.error('Error registering background sync:', error);
    return false;
  }
};

// Check if background sync is supported
export const isBackgroundSyncSupported = () => {
  return 'serviceWorker' in navigator && 'SyncManager' in window;
};

// Notify service worker about online status change
export const notifyServiceWorkerOnline = async () => {
  if (!('serviceWorker' in navigator)) return;
  
  try {
    const registration = await navigator.serviceWorker.ready;
    if (registration.active) {
      registration.active.postMessage({
        type: 'ONLINE_STATUS_CHANGED',
        isOnline: navigator.onLine
      });
    }
  } catch (error) {
    console.error('Error notifying service worker:', error);
  }
};

export default {
  isOfflineModeValid,
  saveOfflineSession,
  clearOfflineSession,
  saveBillsOffline,
  getOfflineBills,
  getAllOfflineBills,
  updateBillStatusOffline,
  saveOfflinePayment,
  getPendingOfflinePayments,
  markPaymentSynced,
  saveAdminFeeOffline,
  getAdminFeeOffline,
  isOnline,
  getOfflineStats,
  getTodayDateString,
  saveAuthTokenForSync,
  clearAuthTokenForSync,
  registerBackgroundSync,
  isBackgroundSyncSupported,
  notifyServiceWorkerOnline
};
